/**
 * When a filing-history row may be called "filed".
 * @file src/web/features/admin/lib/filing-status.ts
 *
 * A return is filed when the portal has given it an ARN. A row whose status
 * reads 'filed' but carries none — a button pressed, a track row without its
 * ARN — is a claim, not a filing; the Overview once told a client GSTR-3B for
 * September 2026 was filed on the strength of one while the portal still had
 * it open. Every screen asks here, so none of them decides for itself.
 *
 * @llm-rule WHEN: A screen shows, counts or branches on whether a return is filed
 * @llm-rule AVOID: Testing `status === 'filed'` on its own
 */
export interface FilingStatusRow {
  status: string;
  arn: string | null;
  arnUnconfirmed?: boolean;
}

/**
 * Filed: the portal's ARN is on the row, and it is not one that was typed in
 * and never returned by a portal check. A claim is not a filing.
 */
export const isFiled = (r: FilingStatusRow): boolean => r.status === 'filed' && !!r.arn && !r.arnUnconfirmed;

/** Marked filed, but with no ARN — nothing the portal has acknowledged. */
export const filedWithoutArn = (r: FilingStatusRow): boolean => r.status === 'filed' && !r.arn;

/** An ARN was typed in and no portal check has returned it yet. */
export const filedUnconfirmed = (r: FilingStatusRow): boolean => r.status === 'filed' && !!r.arn && !!r.arnUnconfirmed;

/** Filed in name only — shown amber, counted as not done. */
export const filedClaimed = (r: FilingStatusRow): boolean => filedWithoutArn(r) || filedUnconfirmed(r);

/** What to call the row in a badge. */
export function statusLabel(r: FilingStatusRow & { source?: string }): string {
  if (filedWithoutArn(r)) return 'filed, no ARN';
  if (filedUnconfirmed(r)) return 'filed, unconfirmed';
  if (isFiled(r)) return r.source === 'portal' ? 'filed on portal' : 'filed';
  return r.status;
}

/** The sentence beside such a row. */
export const VERIFY_ON_PORTAL = 'Filed, unconfirmed: verify on portal';

/** Where a return stands, in the order it travels: prepared, uploaded to the portal, filed. */
export type Stage = 'filed' | 'filed-unconfirmed' | 'filed-no-arn' | 'uploaded' | 'prepared' | 'none';

/**
 * Which stage a return is at.
 *
 * Uploaded is not filed: the portal holds the data and has given no ARN. It is
 * its own stage so a return that went up and was never signed does not read as
 * either "prepared" (it is on the portal) or "filed" (it is not).
 * `uploadedAt` is the stamp the upload step leaves.
 */
export function stageOf(rows: FilingStatusRow[], uploadedAt?: string | null): Stage {
  if (rows.some(isFiled)) return 'filed';
  if (rows.some(filedUnconfirmed)) return 'filed-unconfirmed';
  if (rows.some(filedWithoutArn)) return 'filed-no-arn';
  if (uploadedAt) return 'uploaded';
  if (rows.some((r) => r.status === 'prepared')) return 'prepared';
  return 'none';
}
